import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import type { ProductProfile } from "../../../shared/goals/profile";
import { PhoneNav } from "../../../shared/phone/PhoneNav";
import { Chevron, PnRow, SaveBar } from "../../../shared/phone/Row";
import { Sheet } from "../../../shared/phone/Sheet";
import { Icon, type IconName } from "../../components/ui/Icon";
import { Inline } from "../../components/ui/Rich";
import { AskNative } from "./AskCard";
import { DoneNative } from "./Done";
import { StepLine } from "./FlowHead";
import { LearnLiveNative } from "./LearnLive";
import type { ProfileFlow } from "./ProductProfile";
import { ProfileVersions } from "./ProfileVersions";
import { useQueryScreenScroll } from "./queryScreen";
import { firstLine, host, plural, shortDate } from "./text";
import {
  WK_HINT,
  WK_T,
  allRules,
  assumed,
  changedPart,
  changesLine,
  dropEdit,
  edited,
  emptyWalk,
  hasChanges,
  openAll,
  openQs,
  partState,
  setAnswer,
  setEdit,
  splitFirst,
  togglePb,
  toggleRule,
  walkFrom,
  walkParts,
  type Part,
  type WalkState,
} from "./walk";

/** Each part's icon, the prototype's; a part the method adds gets a page. */
const PART_ICON: Record<string, IconName> = {
  website: "globe",
  what: "box",
  who: "users",
  offer: "pdTag",
  next_step: "target",
  voice: "pdChat",
  proof: "pdChart",
  claims: "shield",
  found: "bulb",
  more: "pen",
};

/** The line under a part's title where the walk has no hint of its own. */
const PART_LEDE: Record<string, string> = {
  website: "Is this the business we should market? Change anything that is off.",
  found: "What it noticed while reading. Nothing here changes your profile unless you say so.",
  more: "What you add here goes in as your words, and outranks what it read.",
};

// --- Words as pieces you tap to change ---

/** One list line or paragraph of a section's words: where it sits, its bullet, its text. */
interface Piece {
  line: number;
  prefix: string;
  text: string;
}

function piecesOf(text: string): Piece[] {
  return text.split("\n").flatMap((l, line): Piece[] => {
    const m = l.match(/^(\s*(?:[-*]|\d+[.)])\s+)(.*)$/);
    if (m) return m[2].trim() ? [{ line, prefix: m[1], text: m[2].trim() }] : [];
    return l.trim() ? [{ line, prefix: "", text: l.trim() }] : [];
  });
}

/**
 * The words with one piece changed. Why: each line typed in its place keeps the piece's bullet, so a list
 * stays a list; emptied, the piece goes.
 */
function withPiece(text: string, piece: Piece, value: string): string {
  const lines = text.split("\n");
  const typed = value
    .split("\n")
    .map((v) => v.trim())
    .filter(Boolean);
  const put = piece.prefix ? typed.map((v) => piece.prefix + v) : typed.flatMap((v, k) => (k ? ["", v] : [v]));
  lines.splice(piece.line, 1, ...put);
  return lines.join("\n");
}

/** What the one editing sheet is changing, and what Done does with the words. */
interface Editing {
  title: string;
  sub?: string;
  label: string;
  value: string;
  placeholder?: string;
  done: (value: string) => void;
}

/** The sheet for words: a 16px box, Cancel and Done in its head. Drawn afresh for each thing it changes. */
function WordsSheet({ editing, onClose }: { editing: Editing; onClose: () => void }) {
  const [v, setV] = useState(editing.value);
  const box = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    // Why: the sheet takes focus as it opens; the words take it right after, with the caret at the end.
    const t = requestAnimationFrame(() => {
      const el = box.current;
      if (!el) return;
      el.focus({ preventScroll: true });
      el.setSelectionRange(el.value.length, el.value.length);
    });
    return () => cancelAnimationFrame(t);
  }, []);
  return (
    <Sheet
      open
      onClose={onClose}
      title={editing.title}
      className="pd-wsheet"
      left={
        <button type="button" className="pn-tbtn" onClick={onClose}>
          Cancel
        </button>
      }
      right={
        <button
          type="button"
          className="pn-tbtn strong"
          onClick={() => {
            editing.done(v);
            onClose();
          }}
        >
          Done
        </button>
      }
    >
      {editing.sub && <p className="pd-ssub">{editing.sub}</p>}
      <div className="pn-grp">
        <textarea ref={box} className="pd-ta" rows={6} aria-label={editing.label} placeholder={editing.placeholder} value={v} onChange={(e) => setV(e.target.value)} />
      </div>
    </Sheet>
  );
}

// --- The walk's parts ---

interface PartProps {
  flow: ProfileFlow;
  p: ProductProfile;
  parts: Part[];
  x: Part;
  edit: (e: Editing) => void;
}

/** A section's words, each piece a row with a pencil; the line under says where it came from. */
function Words({ flow, id, orig, label, partId, source, edit, title }: { flow: ProfileFlow; id: string; orig: string; label: string; partId: string; source: ReactNode; edit: (e: Editing) => void; title?: string }) {
  const { st, setSt } = flow;
  const text = st.ed[id] ?? orig;
  const items = piecesOf(text);
  const was = piecesOf(orig);
  const change = (piece: Piece) =>
    edit({
      title: "Change these words",
      sub: label,
      label,
      value: piece.text,
      done: (v) => setSt((s) => setEdit(s, id, withPiece(s.ed[id] ?? orig, piece, v), orig, partId)),
    });
  return (
    <section>
      {title && <div className="pn-cap">{title}</div>}
      <div className="pn-grp">
        {items.map((pc, k) => (
          <button type="button" className="pn-row wrap pd-wrow" key={`${pc.line}:${k}`} onClick={() => change(pc)}>
            <span className="m">
              <span className="pd-words">
                <Inline text={pc.text} />
              </span>
              {was[k]?.text !== pc.text && <span className="pd-edited">Edited</span>}
            </span>
            <span className="pd-pen" aria-hidden="true">
              <Icon name="pen" />
            </span>
          </button>
        ))}
        {!items.length && (
          <button
            type="button"
            className="pn-row accent"
            onClick={() => edit({ title: "Change these words", sub: label, label, value: "", done: (v) => setSt((s) => setEdit(s, id, v.trim(), orig, partId)) })}
          >
            <span className="pn-ico">
              <Icon name="plus" />
            </span>
            <span className="m">
              <span className="t">Write these words</span>
            </span>
          </button>
        )}
      </div>
      <p className="pn-note">
        {source}
        {st.ed[id] !== undefined && (
          <>
            {" "}
            <button type="button" className="pn-lnk pd-undo" onClick={() => setSt((s) => dropEdit(s, id))}>
              Undo my edit
            </button>
          </>
        )}
      </p>
    </section>
  );
}

function sourceLine(sources: string[]): string {
  return sources.length ? `From ${[...new Set(sources.slice(0, 3).map(host))].join(", ")}` : "Its reading, not a quote.";
}

/** What only the owner knows: each question its own group, its choices to tick and the owner's own answer. */
function Questions({ flow, p, x, edit }: PartProps) {
  const { st, setSt } = flow;
  if (!x.qs.length) return null;
  return (
    <section className="pd-qs">
      <div className="pn-cap">Only you know this</div>
      {x.qs.map((k) => {
        const q = p.questions[k];
        const a = st.ans[k];
        const own = typeof a === "string" && a.trim() ? a : null;
        const write = (start: string) =>
          edit({
            title: "Your answer",
            sub: q.question,
            label: q.question,
            value: start,
            placeholder: "Your answer, in your words",
            done: (v) => {
              const t = v.trim();
              const j = q.options.indexOf(t);
              if (j >= 0) setSt((s) => setAnswer(s, k, j));
              else if (t) setSt((s) => setAnswer(s, k, t));
              else if (own !== null) setSt((s) => setAnswer(s, k, null));
            },
          });
        return (
          <div className="pn-grp" key={k} role="radiogroup" aria-label={q.question} id={`pd-q${k}`}>
            <div className="pn-row static wrap pd-qh">
              <span className="m">
                <span className="t">{q.question}</span>
                {q.why && <span className="s">{q.why}</span>}
              </span>
            </div>
            {q.options.map((o, j) => (
              <button type="button" className="pn-row wrap pd-opt" key={j} role="radio" aria-checked={a === j} onClick={() => setSt((s) => setAnswer(s, k, j))}>
                <span className="m">
                  <span className="t">{o}</span>
                </span>
                {a === j && <Icon name="check" />}
              </button>
            ))}
            {own !== null ? (
              <button type="button" className="pn-row wrap pd-opt" role="radio" aria-checked="true" onClick={() => write(own)}>
                <span className="m">
                  <span className="s">Your answer</span>
                  <span className="t">{own}</span>
                </span>
                <Icon name="check" />
              </button>
            ) : (
              <button type="button" className="pn-row accent" onClick={() => write(typeof a === "number" ? (q.options[a] ?? "") : "")}>
                <span className="pn-ico">
                  <Icon name="plus" />
                </span>
                <span className="m">
                  <span className="t">{q.options.length ? "Your own answer" : "Your answer"}</span>
                </span>
              </button>
            )}
          </div>
        );
      })}
    </section>
  );
}

/** The owner's note on a part: what the website does not say. */
function Note({ flow, x, edit }: PartProps) {
  const { st, setSt } = flow;
  const v = st.note[x.id] ?? "";
  const open = () =>
    edit({
      title: "Your note",
      sub: "What the website does not say, in your words. It outranks anything it read.",
      label: "Your note",
      value: v,
      placeholder: "In your words. This outranks anything it read.",
      done: (val) =>
        setSt((s) => {
          const note = { ...s.note };
          if (val.trim()) note[x.id] = val;
          else delete note[x.id];
          return { ...s, note, noteOpen: null };
        }),
    });
  return (
    <div className="pn-grp">
      {v.trim() ? (
        <button type="button" className="pn-row wrap pd-wrow" onClick={open}>
          <span className="m">
            <span className="pd-q">Your note</span>
            <span className="pd-words">{v}</span>
          </span>
          <span className="pd-pen" aria-hidden="true">
            <Icon name="pen" />
          </span>
        </button>
      ) : (
        <button type="button" className="pn-row accent" onClick={open}>
          <span className="pn-ico">
            <Icon name="plus" />
          </span>
          <span className="m">
            <span className="t">Add what the website does not say</span>
          </span>
        </button>
      )}
    </div>
  );
}

function SitePart(props: PartProps) {
  const { flow, p, x, edit } = props;
  const { st, setSt } = flow;
  const site = (k: "name" | "website", v: string) => setSt((s) => ({ ...s, site: { ...s.site, [k]: v } }));
  return (
    <>
      <div className="pn-grp pd-site">
        <div className="pn-irow">
          <label htmlFor="pd-site-name">Name</label>
          <input id="pd-site-name" value={st.site.name ?? p.business} autoComplete="off" onChange={(e) => site("name", e.target.value)} />
        </div>
        <div className="pn-irow">
          <label htmlFor="pd-site-web">Website</label>
          <input
            id="pd-site-web"
            value={st.site.website ?? p.website}
            inputMode="url"
            autoCapitalize="off"
            autoCorrect="off"
            autoComplete="off"
            onChange={(e) => site("website", e.target.value)}
          />
        </div>
      </div>
      <Words flow={flow} id="summary" orig={p.summary} label={WK_T.summary} partId={x.id} title={WK_T.summary} source="Its summary of everything it read." edit={edit} />
      <Questions {...props} />
      <Note {...props} />
    </>
  );
}

function SectionsPart(props: PartProps) {
  const { flow, p, x, edit } = props;
  return (
    <>
      {x.secs.map((id) => {
        const s = p.sections.find((z) => z.id === id);
        if (!s) return null;
        const label = WK_T[id] ?? s.title;
        return <Words key={id} flow={flow} id={id} orig={s.text} label={label} partId={x.id} title={x.secs.length > 1 ? label : undefined} source={sourceLine(s.sources)} edit={edit} />;
      })}
      <Questions {...props} />
      <Note {...props} />
    </>
  );
}

type Finding = { kind: "pb" | "wo"; k: number } | { kind: "ctas" | "presence" | "market" };
const CTA_TONE: Record<string, [string, string]> = { works: ["good", "Works"], broken: ["danger", "Broken"], "not opened": ["", "Not opened"] };

/** A finding or a rule, opened: what it said, and Not right with the owner's words in its place. */
function FindingSheet({ flow, p, f, onClose }: { flow: ProfileFlow; p: ProductProfile; f: Finding | null; onClose: () => void }) {
  const { st, setSt } = flow;
  const rules = allRules(p);
  let title = "";
  let body: ReactNode = null;
  if (f?.kind === "pb") {
    const pr = p.problems[f.k];
    const [head, rest] = splitFirst(pr.problem);
    const wrong = st.pb[f.k] === "wrong";
    title = "A problem on your pages";
    body = (
      <>
        <div className="pn-grp">
          <div className="pn-row static wrap">
            <span className="m">
              <span className="pd-words">{head}</span>
              {rest && <span className="s">{rest}</span>}
            </span>
          </div>
          <div className="pn-row static wrap">
            <span className="m">
              <span className="s">Where</span>
              <span className="pd-words pd-break">{pr.where}</span>
            </span>
          </div>
          <div className="pn-row static wrap">
            <span className="m">
              <span className="s">Fix</span>
              <span className="pd-words">{pr.fix}</span>
            </span>
          </div>
        </div>
        <NotRight
          on={wrong}
          value={st.pbw[f.k] ?? ""}
          onToggle={() => setSt((s) => togglePb(s, f.k))}
          onChange={(v) => setSt((s) => ({ ...s, pbw: { ...s.pbw, [f.k]: v } }))}
        />
      </>
    );
  } else if (f?.kind === "wo") {
    const off = st.wo[f.k] !== undefined;
    title = "A rule for your kind of business";
    body = (
      <>
        <div className="pn-grp">
          <div className="pn-row static wrap">
            <span className="m">
              <span className="pd-words">{rules[f.k]}</span>
            </span>
          </div>
        </div>
        <p className="pn-note">It travels into every goal for this product. Marked not right, it is taken out.</p>
        <NotRight on={off} value={st.wo[f.k] ?? ""} onToggle={() => setSt((s) => toggleRule(s, f.k))} onChange={(v) => setSt((s) => ({ ...s, wo: { ...s.wo, [f.k]: v } }))} />
      </>
    );
  } else if (f?.kind === "ctas") {
    title = "Buttons on your site";
    body = (
      <div className="pn-grp">
        {p.calls_to_action.map((c, k) => {
          const [tone, label] = CTA_TONE[c.checked] ?? CTA_TONE["not opened"];
          return (
            <div className="pn-row static wrap" key={k}>
              <span className="m">
                <span className="t">{c.label}</span>
                <span className="s pd-break">{c.lands_on}</span>
              </span>
              <span className={tone ? `pn-pill ${tone}` : "pn-pill"}>{label}</span>
            </div>
          );
        })}
      </div>
    );
  } else if (f?.kind === "presence") {
    title = "Where you show up";
    body = (
      <div className="pn-grp">
        {p.presence.map((x, k) => (
          <div className="pn-row static wrap" key={k}>
            <span className="m">
              <span className="t">{x.channel}</span>
              <span className="s">{x.seen}</span>
            </span>
          </div>
        ))}
      </div>
    );
  } else if (f?.kind === "market") {
    title = "Your market";
    body = (
      <div className="pn-grp">
        {p.market.map((m, k) => (
          <div className="pn-row static wrap" key={k}>
            <span className="m">
              <span className="pd-words">{m.fact}</span>
              <span className="s">{host(m.source)}</span>
            </span>
          </div>
        ))}
      </div>
    );
  }
  return (
    <Sheet
      open={!!f}
      onClose={onClose}
      title={title}
      right={
        <button type="button" className="pn-tbtn strong" onClick={onClose}>
          Done
        </button>
      }
    >
      {body}
    </Sheet>
  );
}

/** Not right: a switch, and once on, what is right in the owner's words. */
function NotRight({ on, value, onToggle, onChange }: { on: boolean; value: string; onToggle: () => void; onChange: (v: string) => void }) {
  return (
    <div className="pn-grp pd-gap">
      <div className="pn-row static pd-swrow">
        <span className="m">
          <span className="t">Not right</span>
          <span className="s">{on ? "Your words take its place." : "Everything here counts as right until you say so."}</span>
        </span>
        <button type="button" className="pn-sw" role="switch" aria-checked={on} aria-label="Not right" onClick={onToggle} />
      </div>
      {on && (
        <div className="pn-irow col">
          <label htmlFor="pd-right">What is right?</label>
          <textarea id="pd-right" rows={3} value={value} placeholder="In your words" onChange={(e) => onChange(e.target.value)} />
        </div>
      )}
    </div>
  );
}

function FoundPart({ flow, p }: PartProps) {
  const { st } = flow;
  const [f, setF] = useState<Finding | null>(null);
  const rules = allRules(p);
  const wrongN = p.problems.filter((_, k) => st.pb[k] === "wrong").length;
  const offN = rules.filter((_, k) => st.wo[k] !== undefined).length;
  const count = (v: string) => p.calls_to_action.filter((c) => c.checked === v).length;
  const ctaLine = [count("works") && plural(count("works"), "works", "work"), count("broken") && `${count("broken")} broken`, count("not opened") && `${count("not opened")} not opened`]
    .filter(Boolean)
    .join(" · ");
  return (
    <>
      {p.problems.length > 0 && (
        <section>
          <div className="pn-sh">
            <h2>Problems on your pages</h2>
            {wrongN > 0 && <span className="pd-shn">{wrongN} not right</span>}
          </div>
          <div className="pn-grp">
            {p.problems.map((pr, k) => {
              const wrong = st.pb[k] === "wrong";
              return (
                <button type="button" className="pn-row wrap pd-wrow" key={k} onClick={() => setF({ kind: "pb", k })}>
                  <span className="m">
                    <span className={wrong ? "pd-words pd-struck" : "pd-words"}>{splitFirst(pr.problem)[0]}</span>
                    {wrong && <span className="s pd-mine">{st.pbw[k]?.trim() ? `Not right: ${st.pbw[k].trim()}` : "Not right"}</span>}
                  </span>
                  <Chevron />
                </button>
              );
            })}
          </div>
        </section>
      )}
      {rules.length > 0 && (
        <section>
          <div className="pn-sh">
            <h2>Rules for your kind of business</h2>
            {offN > 0 && <span className="pd-shn">{offN} taken out</span>}
          </div>
          <div className="pn-grp">
            {rules.map((w, k) => {
              const off = st.wo[k] !== undefined;
              return (
                <button type="button" className="pn-row wrap pd-wrow" key={k} onClick={() => setF({ kind: "wo", k })}>
                  <span className="m">
                    <span className={off ? "pd-words pd-struck" : "pd-words"}>{w}</span>
                    {off && <span className="s pd-mine">{st.wo[k].trim() ? `Taken out: ${st.wo[k].trim()}` : "Taken out"}</span>}
                  </span>
                  <Chevron />
                </button>
              );
            })}
          </div>
          <p className="pn-note">These travel into every goal for this product. One marked not right is taken out.</p>
        </section>
      )}
      {(p.calls_to_action.length > 0 || p.presence.length > 0 || p.market.length > 0) && (
        <div className="pn-grp">
          {p.calls_to_action.length > 0 && <PnRow onClick={() => setF({ kind: "ctas" })} title="Buttons on your site" sub={ctaLine} chevron />}
          {p.presence.length > 0 && <PnRow onClick={() => setF({ kind: "presence" })} title="Where you show up" sub={plural(p.presence.length, "place")} chevron />}
          {p.market.length > 0 && <PnRow onClick={() => setF({ kind: "market" })} title="Your market" sub={plural(p.market.length, "fact")} chevron />}
        </div>
      )}
      <FindingSheet flow={flow} p={p} f={f} onClose={() => setF(null)} />
    </>
  );
}

function MorePart(props: PartProps) {
  const { flow, parts, edit } = props;
  const { st, setSt } = flow;
  const base = `/products/${encodeURIComponent(flow.id)}/profile`;
  const open = openAll(parts, st).filter((o) => o.x.id !== "more");
  const unchecked = parts.filter((y) => !st.ok[y.id] && y.id !== "more");
  const extra = () =>
    edit({
      title: "Anything else",
      sub: "Anything else we should know",
      label: "Anything else we should know",
      value: st.extra,
      placeholder: "Offers you can make, what you will never say, who your best clients are, what makes you different...",
      done: (v) => setSt((s) => ({ ...s, extra: v })),
    });
  return (
    <>
      <Questions {...props} />
      <section>
        <div className="pn-cap">Anything else we should know</div>
        <div className="pn-grp">
          {st.extra.trim() ? (
            <button type="button" className="pn-row wrap pd-wrow" onClick={extra}>
              <span className="m">
                <span className="pd-words">{st.extra}</span>
              </span>
              <span className="pd-pen" aria-hidden="true">
                <Icon name="pen" />
              </span>
            </button>
          ) : (
            <button type="button" className="pn-row accent" onClick={extra}>
              <span className="pn-ico">
                <Icon name="plus" />
              </span>
              <span className="m">
                <span className="t">Add it in your words</span>
              </span>
            </button>
          )}
        </div>
      </section>
      <section>
        <div className="pn-cap">Your changes</div>
        <div className="pn-grp">
          <div className="pn-row static wrap">
            <span className="m">
              <span className="pd-words">{changesLine(st)}</span>
              {!open.length && unchecked.length > 0 && !st.base && (
                <span className="s">
                  {plural(unchecked.length, "part")} not checked yet. Confirming keeps {unchecked.length > 1 ? "them" : "it"} as found.
                </span>
              )}
            </span>
          </div>
          {open.map((o) => (
            <PnRow key={o.x.id} to={`${base}?part=${encodeURIComponent(o.x.id)}`} title={o.x.title} sub={`${plural(o.n, "question")} open`} iconTone="warn" chevron />
          ))}
        </div>
      </section>
    </>
  );
}

// --- The screens ---

/** A part's line on the profile: its first words, or what it holds. */
function gist(x: Part, p: ProductProfile, st: WalkState): string {
  if (x.kind === "site") return firstLine(st.ed.summary ?? p.summary);
  if (x.kind === "found") {
    const bits = [p.problems.length && plural(p.problems.length, "problem"), allRules(p).length && plural(allRules(p).length, "rule")].filter(Boolean);
    return bits.length ? bits.join(" · ") : "What it noticed while reading";
  }
  if (x.kind === "more") return st.extra.trim() ? firstLine(st.extra) : "Your own words, if anything is missing";
  const s = p.sections.find((z) => z.id === x.secs[0]);
  return s ? firstLine(st.ed[s.id] ?? s.text) : "";
}

/** Where a part stands, said first on its line: checked, changed, or waiting for the owner. */
function Marker({ x, st, p, edit }: { x: Part; st: WalkState; p: ProductProfile; edit: boolean }) {
  const s = edit ? (changedPart(x, st) ? "edited" : null) : partState(x, st, p);
  const [tone, word] = s === "ok" ? ["good", "Checked"] : s === "edited" ? ["violet", edit ? "Edited" : "Your edits"] : s === "you" ? ["warn", "Needs you"] : [null, null];
  if (!word) return null;
  return (
    <>
      <b className={`pd-mk ${tone}`}>{word}</b>
      {" · "}
    </>
  );
}

/**
 * The walk on a phone or an iPad: the profile as a list of its parts, and each part on a screen of its own
 * (`?part=`). A draft is confirmed from the list or the last part; a confirmed profile saves from a save bar
 * that slides up once something changed.
 */
function WalkNative({ flow, profile, edit }: { flow: ProfileFlow; profile: ProductProfile; edit: boolean }) {
  const { id, product, view, st, setSt, busy } = flow;
  const navigate = useNavigate();
  const [q] = useSearchParams();
  const [editing, setEditing] = useState<Editing | null>(null);
  // Why: the owner already confirmed this profile, so nothing in it waits for them as the agent's best reading.
  const p = useMemo(() => (edit ? { ...profile, sections: profile.sections.map((s) => ({ ...s, status: "found" as const })) } : profile), [profile, edit]);
  const parts = useMemo(() => walkParts(p), [p]);
  // Why: a saved profile reopens as the owner left it: every part checked, every answer, review and note in place.
  const fresh = edit && st.base === undefined;
  useEffect(() => {
    if (fresh) setSt((s) => (s.base !== undefined ? s : { ...walkFrom(p, parts), at: s.at }));
  }, [fresh, p, parts, setSt]);

  const name = product?.name ?? "";
  const base = `/products/${encodeURIComponent(id)}/profile`;
  const partAt = (k: number) => `${base}?part=${encodeURIComponent(parts[k].id)}`;
  const i = parts.findIndex((x) => x.id === q.get("part"));
  const open = openAll(parts, st).length;
  const unchecked = parts.filter((y) => !st.ok[y.id] && y.id !== "more").length;
  const confirmBtn = (
    <button type="button" className="pn-btn pri wide" disabled={open > 0 || busy} onClick={() => void flow.confirm()}>
      Confirm the profile
      <Icon name="check" />
    </button>
  );
  const confirmNote = open ? `${plural(open, "question")} still open` : unchecked ? `${plural(unchecked, "part")} not checked yet. Confirming keeps ${unchecked > 1 ? "them" : "it"} as found.` : null;
  const foot = edit ? (
    <SaveBar
      dirty={hasChanges(st)}
      saving={busy}
      label="Save changes"
      onSave={() => void flow.saveChanges(true)}
      onDiscard={() => setSt((s) => ({ ...walkFrom(p, parts), at: s.at }))}
    />
  ) : null;

  let screen;
  if (i >= 0) {
    const x = parts[i];
    const props: PartProps = { flow, p, parts, x, edit: setEditing };
    const last = i === parts.length - 1;
    const here = openQs(x, st).length;
    const next = () => navigate(partAt(i + 1), { replace: true });
    let main: ReactNode;
    let note: string | null = null;
    if (last) {
      main = confirmBtn;
      note = confirmNote;
    } else if (here) {
      main = (
        <button type="button" className="pn-btn wide" onClick={next}>
          Skip for now
          <Icon name="right" />
        </button>
      );
      note = `${plural(here, "question")} open`;
    } else
      main = (
        <button
          type="button"
          className="pn-btn pri wide"
          onClick={() => {
            setSt((s) => ({ ...s, ok: { ...s.ok, [x.id]: true } }));
            next();
          }}
        >
          {x.kind === "found" ? "Next" : edited(x, st) ? "Save and next" : "Looks right"}
          <Icon name="right" />
        </button>
      );
    screen = (
      <div className="pn-screen">
        <PhoneNav title={x.title} large back={{ to: base, label: "Profile" }} sub={WK_HINT[x.id] ?? PART_LEDE[x.id]} />
        <div className="pn-stack tight">
          {!edit && assumed(x, p) && !st.ok[x.id] && (
            <p className="pd-flag">
              <Icon name="info" />
              Its best reading: please check
            </p>
          )}
          {x.kind === "site" && <SitePart {...props} />}
          {x.kind === "sections" && <SectionsPart {...props} />}
          {x.kind === "found" && <FoundPart {...props} />}
          {x.kind === "more" && <MorePart {...props} />}
        </div>
        {foot ?? (
          <div className="pn-foot">
            {main}
            {note && <p className="pn-fnote">{note}</p>}
          </div>
        )}
      </div>
    );
  } else {
    const c = view?.confirmed;
    const done = parts.filter((y) => st.ok[y.id]).length;
    screen = (
      <div className="pn-screen">
        <PhoneNav title={`${name} profile`} large back={{ to: `/products/${encodeURIComponent(id)}`, label: name }} sub={edit ? <StepLine at={4} word="Confirmed" /> : <StepLine at={3} word="Confirm" />} />
        <div className="pn-stack">
          {edit && c && (
            <div className="pn-grp">
              <PnRow onClick={() => flow.setVersions(true)} icon={<Icon name="clock" />} iconTone="plain" title={`Version ${c.version}`} sub={c.confirmedAt ? `Confirmed ${shortDate(c.confirmedAt)}` : undefined} chevron />
            </div>
          )}
          <section>
            <div className="pn-sh">
              <h2>What the agent knows</h2>
              {!edit && (
                <span className="pd-shn">
                  {done} of {parts.length} checked
                </span>
              )}
            </div>
            <div className="pn-grp">
              {parts.map((x, k) => (
                <Link className="pn-row pd-prt" key={x.id} to={partAt(k)}>
                  <span className="pn-ico">
                    <Icon name={PART_ICON[x.id] ?? "doc"} />
                  </span>
                  <span className="m">
                    <span className="t">{x.title}</span>
                    <span className="s">
                      <Marker x={x} st={st} p={p} edit={edit} />
                      {gist(x, p, st)}
                    </span>
                  </span>
                  <Chevron />
                </Link>
              ))}
            </div>
            <p className="pn-note">{edit ? `Every goal for ${name} reads this first.` : "Check each part. Your words outrank what it read."}</p>
          </section>
        </div>
        {foot ?? (
          <div className="pn-foot">
            {confirmBtn}
            {confirmNote && <p className="pn-fnote">{confirmNote}</p>}
          </div>
        )}
      </div>
    );
  }

  return (
    <>
      {screen}
      {editing && <WordsSheet key={editing.label + editing.value} editing={editing} onClose={() => setEditing(null)} />}
      {edit && flow.versions && view?.confirmed && (
        <ProfileVersions
          productId={id}
          name={name}
          onto={view.confirmed.version}
          onClose={() => flow.setVersions(false)}
          say={flow.toast}
          onUsed={async (from, now) => {
            flow.setVersions(false);
            // Why: the walk starts again from the words now in use, so it reads them first.
            await flow.load();
            setSt(emptyWalk());
            flow.toast(`Version ${from}'s words are back, as version ${now}. Every goal for ${name} reads them from now.`);
          }}
        />
      )}
    </>
  );
}

/**
 * A product's profile on a phone or an iPad, one screen per step of the flow: learning, the question of
 * which business, the walk to confirm or change it, and what comes next. Where it stands is said in words.
 */
export function ProfileNative({ flow }: { flow: ProfileFlow }) {
  const { id, product, view, loadErr, st, busy, askRun, justConfirmed, startNote, setStartNote, activeRun, watch, answer, start, toastNode } = flow;
  const [q] = useSearchParams();
  useQueryScreenScroll(q.get("part") ?? "profile");
  const back = { to: `/products/${encodeURIComponent(id)}`, label: product?.name ?? "" };

  let screen: ReactNode;
  if (loadErr || !product || !view) {
    screen = (
      <div className="pn-screen">
        <PhoneNav title="" back={back} />
        {loadErr ? (
          <div className="pn-empty">
            <b>{loadErr.status === 404 ? "This product is not here" : "Couldn't load this product"}</b>
            {loadErr.status === 404 ? "It does not exist, or it belongs to another account." : loadErr.message}
          </div>
        ) : (
          <p className="pn-note">Loading…</p>
        )}
      </div>
    );
  } else if (justConfirmed) {
    screen = (
      <div className="pn-screen">
        <PhoneNav title="Profile confirmed" large back={back} sub={<StepLine at={4} word="Confirmed" />} />
        <div className="pn-stack">
          <p className="pn-note pd-lede">{product.name} is in your library. Every goal for it reads this first.</p>
          <DoneNative profile={justConfirmed.profile} st={st} version={justConfirmed.version} />
        </div>
        <div className="pn-foot">
          <Link className="pn-btn pri wide" to={`/products/${encodeURIComponent(id)}/goals/new`}>
            Start the first goal
            <Icon name="right" />
          </Link>
        </div>
      </div>
    );
  } else if (activeRun) {
    screen = (
      <div className="pn-screen">
        <PhoneNav title="Learning the business" large back={back} sub={<StepLine at={2} word="Learn" />} />
        <div className="pn-stack tight">
          <p className="pn-note pd-lede">{product.description ? `${product.name} · ${product.description}` : product.name}</p>
          <LearnLiveNative run={watch.run} events={watch.events} />
          {watch.error && <p className="pn-note">Couldn't read the run: {watch.error}</p>}
        </div>
      </div>
    );
  } else if (view.draft?.question) {
    screen = (
      <div className="pn-screen">
        <PhoneNav title="Is this you?" large back={back} sub={<StepLine at={2} word="Learn" />} />
        <div className="pn-stack tight">
          <p className="pn-note pd-lede">More than one business is called “{view.draft.question.business}”. It stopped to ask instead of guessing.</p>
          <AskNative q={view.draft.question} run={askRun} busy={busy} onAnswer={answer} />
        </div>
      </div>
    );
  } else if (view.draft?.profile) {
    screen = <WalkNative flow={flow} profile={view.draft.profile} edit={false} />;
  } else if (view.confirmed) {
    screen = <WalkNative flow={flow} profile={view.confirmed.profile} edit />;
  } else {
    const stopped = view.learnRun && view.learnRun.state !== "done" ? view.learnRun : null;
    screen = (
      <div className="pn-screen">
        <PhoneNav title={`${product.name} profile`} large back={back} sub={<StepLine at={2} word="Learn" />} />
        <div className="pn-stack tight">
          <p className="pn-note pd-lede">{stopped ? "The agent stopped before it finished. Try again." : "Flashadz has not learned this business yet."}</p>
          {stopped?.error && <p className="pd-flag">{stopped.error}</p>}
          <div className="pn-grp">
            <div className="pn-irow col">
              <label htmlFor="pd-start-note">Anything you already have</label>
              <textarea
                id="pd-start-note"
                rows={4}
                placeholder="Optional. Notes, an FAQ or a pitch. Your words outrank public pages."
                value={startNote}
                onChange={(e) => setStartNote(e.target.value)}
              />
            </div>
          </div>
        </div>
        <div className="pn-foot">
          <button type="button" className="pn-btn pri wide" disabled={busy} onClick={start}>
            Learn the business
            <Icon name="right" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {screen}
      {toastNode}
    </>
  );
}
